import Download from "@mui/icons-material/Download";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Tooltip } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import classNames from "classnames";
import { formatFileName, getJsonOrFalse } from "helpers/utils";
import type { CSSProperties } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import ButtonCollapse from "../ButtonCollapse";
import LastElementTable from "../LastElementTable";
import TextSkeleton from "../TextSkeleton";
import styles from "./style.module.scss";

/**
 * Type of value if it is an object.
 */
type TRecordData = { text: string; icon: JSX.Element; style?: CSSProperties; tooltip?: string };

/**
 * Type of the values for each pair.
 */
export type TGridData = string | TRecordData;

interface IInformationGrid {
    /**
     * Data that will populate the grid (keys and values)
     */
    data: Record<string, TGridData>;
    /**
     * Title of the block.
     */
    title: string;
    titleIcon?: JSX.Element;
    /**
     * This props is used in the beneficiar general informations. Key correspond to a sensitive information
     * field. It taked a boolean value.
     * True to hide info and false to show info. It's related to the hide/show eye button.
     */
    maskedInfo?: any;
    /**
     * Set maskedInfo key value to true or false.
     */
    setMaskedInfo?: any;
    portal?: "author";
    style?: CSSProperties;
    headerActions?: JSX.Element[];
    /**
     * Extra lines data to display under the block info.
     */
    extraInfosLines?: { label: string; value: string }[];
    /**
     * Extra label to display under the block info.
     */
    extraInfosLabel?: React.ReactNode;
    /** Keep labels, replace values with text skeletons. */
    isLoading?: boolean;
    /** When loading, how many placeholder fields to show (defaults to the data keys). */
    loadingFieldCount?: number;
}

/**
 * Responsive component that displays block of data saved in Title or Beneficiary for its details page.
 */
const InformationGrid = ({
    data,
    title,
    titleIcon,
    maskedInfo,
    setMaskedInfo,
    portal,
    style,
    headerActions,
    extraInfosLines,
    extraInfosLabel = null,
    isLoading = false,
    loadingFieldCount,
}: IInformationGrid) => {
    const [t] = useTranslation();
    const formattedData = useMemo(() => Object.keys(data), [data]);

    const maskedInfoTypes = useMemo(() => {
        return maskedInfo ? Object.keys(maskedInfo) : [];
    }, [maskedInfo]);

    const renderVisibilityBtn = (key: string) => {
        if (maskedInfo[key] === true) {
            return (
                <div className={styles["mask-svg"]} onClick={() => handleVisibility(key)}>
                    <Visibility />
                </div>
            );
        } else {
            return (
                <div className={styles["mask-svg"]} onClick={() => handleVisibility(key)}>
                    <VisibilityOff />
                </div>
            );
        }
    };

    const handleVisibility = (key: string) => {
        setMaskedInfo({ ...maskedInfo, [key]: !maskedInfo[key] });
    };

    /**
     * Downloads file saved within a custom field.
     * @param item Data of the file custom field.
     */
    const handleFileDownload = async (item: { fileName: string; link: string }) => {
        const file = await fetch(item.link);
        const fileBlob = await file.blob();
        const fileURL = URL.createObjectURL(fileBlob);
        const fileName = item.fileName;

        const link = document.createElement("a");
        link.href = fileURL;
        link.download = fileName;
        link.click();
    };

    return (
        <>
            {extraInfosLines && extraInfosLines.length > 0 && (
                <div className={styles["extra-infos-container"]}>
                    {extraInfosLabel}
                    <div className={styles["extra-infos-block"]}>
                        {extraInfosLines?.map((item) => {
                            return (
                                <div key={item.label} className={styles["extra-info-line"]}>
                                    <div className={styles["extra-info-label"]}>{item.label}</div>
                                    <div className={styles["extra-info-value"]}>{item.value}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            <div
                className={`${styles["information-block"]} ${portal === "author" ? styles["bportal"] : ""}`}
                style={style}
            >
                <div className={styles["block-header"]}>
                    <div className={styles["header-left"]}>
                        {titleIcon}
                        <h3 className={styles["info-title"]}>
                            {isLoading && !title ? <TextSkeleton width="9em" /> : title}
                        </h3>
                    </div>

                    {/** If we provide actions they appear on right of header. (I think it really only works for two actions max.) */}
                    {isLoading && !title ? (
                        <div className={styles["block-actions"]}>
                            <TextSkeleton width="4.5em" />
                        </div>
                    ) : (
                        headerActions &&
                        headerActions.length > 0 && (
                            <div className={styles["block-actions"]}>
                                {headerActions.map((item) => {
                                    return item;
                                })}
                            </div>
                        )
                    )}
                </div>
                <Grid container columnSpacing={3} sx={{ padding: "0.5rem" }} rowSpacing={2}>
                    {isLoading && loadingFieldCount != null
                        ? Array.from({ length: loadingFieldCount }, (_, index) => (
                              <Grid xs={6} sm={4} md={6} lg={3} key={`loading-field-${index}`}>
                                  <div className={styles["info-column"]}>
                                      <span className={styles["info-key"]}>
                                          <TextSkeleton width={`${3.5 + (index % 3)}em`} />
                                      </span>
                                      <p className={styles["info-value"]}>
                                          <TextSkeleton width={`${55 + (index % 4) * 8}%`} />
                                      </p>
                                  </div>
                              </Grid>
                          ))
                        : formattedData.map((item) => {
                              const fileData = getJsonOrFalse(data[item] as string);
                              const formattedName = formatFileName(fileData?.fileName ?? "");

                              const element = data[item];
                              const shouldBeMasked = maskedInfoTypes.length > 0 && maskedInfoTypes.includes(item);

                              return (
                                  <Grid xs={6} sm={4} md={6} lg={3} key={`field-${item}`}>
                                      <div className={styles["info-column"]}>
                                          <Tooltip title={item.length > 30 ? item : ""}>
                                              <span className={styles["info-key"]}>{item}</span>
                                          </Tooltip>

                                          {isLoading ? (
                                              <p className={styles["info-value"]}>
                                                  <TextSkeleton width="70%" />
                                              </p>
                                          ) : typeof element === "string" || element === null ? (
                                              fileData ? ( // If string, check if the data is of the type JSON > for links
                                                  <Tooltip title={formattedName}>
                                                      <div
                                                          onClick={() => handleFileDownload(fileData)}
                                                          className={styles["link-block"]}
                                                      >
                                                          <span className={styles["link-name"]}>{formattedName}</span>
                                                          <Download className={styles["download-icon"]} />
                                                      </div>
                                                  </Tooltip>
                                              ) : (
                                                  <p // If not handle as simple string.
                                                      className={classNames({
                                                          [styles["info-value"]]: true,
                                                          [styles["flex"]]: shouldBeMasked,
                                                      })}
                                                  >
                                                      {element || "-"}
                                                      {element === "-"
                                                          ? null
                                                          : shouldBeMasked && <> {renderVisibilityBtn(item)}</>}
                                                  </p>
                                              )
                                          ) : (
                                              <Tooltip title={element.tooltip ?? ""} arrow>
                                                  {/** This handles data of Record type */}
                                                  <div style={element.style} className={styles["data-block"]}>
                                                      <span>{element.text ?? ""}</span>
                                                      {element.icon}
                                                  </div>
                                              </Tooltip>
                                          )}
                                      </div>
                                  </Grid>
                              );
                          })}
                </Grid>
            </div>
        </>
    );
};

export default InformationGrid;
